import { useState } from 'react'
import { GameState, Mugong, Item, MugongSlot } from '../store/types'
import { apiMugongLearn, apiMugongEquip } from '../server/ServiceCall'

const GRADE_COLOR: Record<string, string> = {
  COMMON:'#a0a0a0', UNCOMMON:'#60c060', RARE:'#4090e0',
  EPIC:'#a060e0', LEGEND:'#e0a030', MYTH:'#e04060',
}
const GRADE_KO: Record<string, string> = {
  COMMON:'일반', UNCOMMON:'고급', RARE:'희귀', EPIC:'영웅', LEGEND:'전설', MYTH:'신화',
}
const ELEMENT_COLOR: Record<string, string> = {
  '木':'#60a040', '火':'#e06030', '土':'#c0a040', '金':'#c8c840',
  '水':'#4080c0', '氷':'#60c0e0', '毒':'#80c040', '正':'#80a0e0',
  '邪':'#a060a0', '魔':'#c04040',
}
const CATEGORY_KO: Record<string, string> = {
  '心法':'심법', '步法':'보법', '輕功':'경공', '拳法':'권법',
  '劍法':'검법', '刀法':'도법', '掌法':'장법',
}

const SLOT_LABEL: Record<MugongSlot, string> = {
  SIMBEOP: '심법',
  BOBEOP:  '보법',
  ATTACK:  '공격',
}

function categoryToSlot(category: string): MugongSlot {
  if (category === '心法') return 'SIMBEOP'
  if (category === '步法' || category === '輕功') return 'BOBEOP'
  return 'ATTACK'
}

interface MugongTooltip { mg: Mugong; x: number; y: number }
interface BookTooltip   { book: Item; x: number; y: number }

interface Props {
  gs:        GameState
  token:     string
  onRefresh: () => void
}

const GRADE_ORDER: Record<string, number> = {
  MYTH:0, LEGEND:1, EPIC:2, RARE:3, UNCOMMON:4, COMMON:5,
}
const CAT_ORDER = ['心法','劍法','刀法','掌法','拳法','指法','步法','輕功']

export default function TabMugong({ gs, token, onRefresh }: Props) {
  const [selected, setSelected] = useState<Mugong | null>(null)
  const [catTab,   setCatTab]   = useState<string>('전체')
  const [busy,     setBusy]     = useState(false)
  const [msg,      setMsg]      = useState('')
  const [msgOk,    setMsgOk]    = useState(false)
  const [tooltip,  setTooltip]  = useState<MugongTooltip | BookTooltip | null>(null)

  const books      = gs.inventory.filter(item => item.category === 'MUGONG_BOOK' && item.mugongId)
  const learnedIds = new Set(gs.mugongs.map(m => m.mugongId))

  const sorted      = [...gs.mugongs].sort((a, b) => (GRADE_ORDER[a.grade] ?? 9) - (GRADE_ORDER[b.grade] ?? 9))
  const presentCats = CAT_ORDER.filter(c => sorted.some(m => m.category === c))
  const seenCats    = sorted.reduce<string[]>((acc, m) => acc.includes(m.category) ? acc : [...acc, m.category], [])
  const otherCats   = seenCats.filter(c => !CAT_ORDER.includes(c))
  const tabs        = ['전체', ...presentCats, ...otherCats]
  const visible     = catTab === '전체' ? sorted : sorted.filter(m => m.category === catTab)

  const equippedSlots: Partial<Record<MugongSlot, Mugong>> = {}
  for (const mg of gs.mugongs) {
    if (mg.equippedSlot) equippedSlots[mg.equippedSlot] = mg
  }

  function showMsg(m: string, ok = false) { setMsg(m); setMsgOk(ok); setTimeout(() => setMsg(''), 2500) }

  async function doLearn(invId: number) {
    setBusy(true)
    try {
      const res = await apiMugongLearn(token, String(invId))
      showMsg(res.resultMessage, res.resultCode === '00')
      if (res.resultCode === '00') onRefresh()
    } catch { showMsg('서버 오류') }
    finally { setBusy(false) }
  }

  async function doEquip(mg: Mugong) {
    setBusy(true)
    try {
      const res = await apiMugongEquip(token, mg.mugongId)
      showMsg(res.resultMessage, res.resultCode === '00')
      if (res.resultCode === '00') onRefresh()
    } catch { showMsg('서버 오류') }
    finally { setBusy(false) }
  }

  function isMugongTooltip(t: any): t is MugongTooltip { return 'mg' in t }

  function Tooltip() {
    if (!tooltip) return null
    const { x, y } = tooltip
    const left = x + 170 > window.innerWidth ? x - 168 : x + 12
    const top  = Math.min(y - 10, window.innerHeight - 200)

    if (isMugongTooltip(tooltip)) {
      const mg = tooltip.mg
      return (
        <div className="inven-tooltip" style={{ left, top, position:'fixed', zIndex:9999, pointerEvents:'none' }}>
          <div className="inven-tooltip-name" style={{ color: GRADE_COLOR[mg.grade] }}>{mg.name}</div>
          <div className="inven-tooltip-grade">{GRADE_KO[mg.grade] ?? mg.grade}</div>
          <div className="inven-tooltip-cat">
            {CATEGORY_KO[mg.category] ?? mg.category}
            {mg.element && (
              <span style={{ marginLeft:4, color: ELEMENT_COLOR[mg.element] }}>·{mg.element}</span>
            )}
          </div>
          <div className="inven-tooltip-stats">
            <span>성급 {mg.star}성</span>
            {mg.atkBonus > 0 && <span>공 +{mg.atkBonus * mg.star} (+{mg.atkBonus}/성)</span>}
            {mg.defBonus > 0 && <span>방 +{mg.defBonus * mg.star} (+{mg.defBonus}/성)</span>}
            {mg.hpBonus  > 0 && <span>체 +{mg.hpBonus  * mg.star} (+{mg.hpBonus}/성)</span>}
            {mg.spdBonus > 0 && <span>속 +{mg.spdBonus * mg.star} (+{mg.spdBonus}/성)</span>}
          </div>
          <div style={{ fontSize:10, color: mg.star >= mg.maxStar ? '#e0c060' : '#706050', marginTop:3 }}>
            {mg.star >= mg.maxStar ? '경험치 MAX' : `경험치 ${mg.exp}`}
          </div>
        </div>
      )
    }

    const book = (tooltip as BookTooltip).book
    return (
      <div className="inven-tooltip" style={{ left, top, position:'fixed', zIndex:9999, pointerEvents:'none' }}>
        <div className="inven-tooltip-name" style={{ color: GRADE_COLOR[book.grade] }}>{book.name}</div>
        <div className="inven-tooltip-grade">{GRADE_KO[book.grade] ?? book.grade} 비급</div>
        {book.qty > 1 && <div style={{ fontSize:10, color:'#706050', marginTop:2 }}>수량 {book.qty}개</div>}
      </div>
    )
  }

  const SLOTS: MugongSlot[] = ['SIMBEOP', 'BOBEOP', 'ATTACK']

  return (
    <div className="tab-mugong" onMouseMove={e => {
      if (tooltip) setTooltip(t => t ? { ...t, x: e.clientX, y: e.clientY } : null)
    }}>
      <Tooltip />
      {msg && <div className={`inven-msg${msgOk ? ' ok' : ''}`}>{msg}</div>}

      {/* 장착 슬롯 */}
      <section className="card-section">
        <div className="card-title">장착 무공 <span style={{ fontSize:10, color:'#706050' }}>(장착된 무공만 전투력에 반영)</span></div>
        <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
          {SLOTS.map(slot => {
            const mg = equippedSlots[slot]
            return (
              <div key={slot} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 8px', background:'#1a1208', borderRadius:4, border:'1px solid #3a2e18' }}>
                <span style={{ fontSize:11, color:'#806050', minWidth:28 }}>{SLOT_LABEL[slot]}</span>
                {mg ? (
                  <>
                    {mg.element && (
                      <span className="mugong-element" style={{ background: ELEMENT_COLOR[mg.element] ?? '#606060', fontSize:9 }}>
                        {mg.element}
                      </span>
                    )}
                    <span style={{ color: GRADE_COLOR[mg.grade], fontSize:12, flex:1 }}>{mg.name}</span>
                    <span style={{ fontSize:10, color:'#706050' }}>{mg.star}성</span>
                    <button
                      className="action-btn drop"
                      style={{ padding:'1px 8px', fontSize:10 }}
                      disabled={busy}
                      onClick={() => doEquip(mg)}
                    >해제</button>
                  </>
                ) : (
                  <span style={{ fontSize:11, color:'#504030', flex:1 }}>— 비어있음</span>
                )}
              </div>
            )
          })}
        </div>
      </section>

      {/* 습득 무공 목록 */}
      {gs.mugongs.length === 0 ? (
        <div className="mugong-empty">습득한 무공이 없습니다.<br/>비급 아이템을 사용해 무공을 습득하세요.</div>
      ) : (
        <section className="card-section">
          <div className="card-title">습득 무공</div>

          <div className="train-cat-tabs">
            {tabs.map(tab => (
              <button
                key={tab}
                className={`train-cat-tab${catTab === tab ? ' active' : ''}`}
                onClick={() => { setCatTab(tab); setSelected(null) }}
              >
                {CATEGORY_KO[tab] ?? tab}
              </button>
            ))}
          </div>

          <div className="mugong-list">
            {visible.map(mg => {
              const mySlot    = categoryToSlot(mg.category)
              const isEquipped = mg.equippedSlot === mySlot
              return (
                <div key={mg.mugongId}
                  className={`mugong-item${selected?.mugongId===mg.mugongId?' selected':''}${isEquipped?' active-training':''}`}
                  onClick={() => setSelected(selected?.mugongId===mg.mugongId ? null : mg)}
                  onMouseEnter={e => setTooltip({ mg, x: e.clientX, y: e.clientY })}
                  onMouseLeave={() => setTooltip(null)}
                >
                  <div className="mugong-name-row">
                    {mg.element && (
                      <span className="mugong-element" style={{ background: ELEMENT_COLOR[mg.element] }}>
                        {mg.element}
                      </span>
                    )}
                    <span className="mugong-name" style={{ color: GRADE_COLOR[mg.grade] }}>{mg.name}</span>
                    <span style={{ fontSize:10, color:'#806050', marginLeft:4 }}>
                      {CATEGORY_KO[mg.category] ?? mg.category}
                    </span>
                    <div className="mugong-stars" style={{ marginLeft:'auto' }}>
                      {Array.from({ length: mg.star }).map((_, i) => (
                        <span key={i} className="star on">★</span>
                      ))}
                      <span className="mugong-star-label">{mg.star}성</span>
                    </div>
                  </div>
                  <div className="mugong-bonus-row">
                    {mg.atkBonus  > 0 && <span>공+{mg.atkBonus * mg.star}</span>}
                    {mg.defBonus  > 0 && <span>방+{mg.defBonus * mg.star}</span>}
                    {mg.hpBonus   > 0 && <span>체+{mg.hpBonus  * mg.star}</span>}
                    {mg.spdBonus  > 0 && <span>속+{mg.spdBonus * mg.star}</span>}
                    {isEquipped && <span style={{ color:'#60c060', marginLeft:'auto' }}>장착 중</span>}
                    <span className="mugong-exp" style={{ color: mg.star >= mg.maxStar ? '#e0c060' : undefined, marginLeft: isEquipped ? 0 : 'auto' }}>
                      {mg.star >= mg.maxStar ? 'MAX' : `경험 ${mg.exp}`}
                    </span>
                  </div>
                  {selected?.mugongId === mg.mugongId && (
                    <button
                      className={`action-btn ${isEquipped ? 'drop' : 'equip'}`}
                      style={{ marginTop:8, width:'100%' }}
                      disabled={busy}
                      onClick={e => { e.stopPropagation(); doEquip(mg) }}
                    >
                      {isEquipped ? `${SLOT_LABEL[mySlot]} 슬롯 해제` : `${SLOT_LABEL[mySlot]} 슬롯 장착`}
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        </section>
      )}

      {/* 미습득 비급 */}
      {books.length > 0 && (
        <section className="card-section">
          <div className="card-title">미습득 비급</div>
          <div className="mugong-list">
            {books.map(book => {
              const alreadyLearned = learnedIds.has(book.mugongId!)
              return (
                <div key={book.invId} className="mugong-item"
                  onMouseEnter={e => setTooltip({ book, x: e.clientX, y: e.clientY })}
                  onMouseLeave={() => setTooltip(null)}
                >
                  <div className="mugong-name-row">
                    <span className="mugong-name" style={{ color: GRADE_COLOR[book.grade] }}>{book.name}</span>
                    {alreadyLearned
                      ? <span style={{ color:'#808070', fontSize:11, marginLeft:'auto' }}>이미 습득</span>
                      : <button className="action-btn equip" style={{ marginLeft:'auto', padding:'2px 10px' }}
                          disabled={busy} onClick={() => doLearn(book.invId)}>습득</button>
                    }
                  </div>
                </div>
              )
            })}
          </div>
        </section>
      )}
    </div>
  )
}
